// Type-only import: erased at compile time, so this never creates a runtime
// circular dependency with governed-capability-manifest.ts even though that
// file imports these types back (see the re-export note below).
import type { CapabilityRequirement } from './governed-capability-manifest';

/**
 * #7322 increment 2 size-law split — moved out of governed-capability-manifest.ts
 * (over its own 600-line file ceiling; every touch must leave it smaller than it
 * found it — see the #5850 precedent split at the top of that file) so the new
 * `metricGrain` field on `GovernedCapabilityIntent` had room to land without
 * growing the touched file. Content-identical move: `governed-capability-
 * manifest.ts` imports and re-exports every name below, so its own public API
 * (and every existing `@helix/lib` import site) is unchanged.
 */

// A manifest-declared default sensitivity band for a scenario parameter. When a
// scenario-class question reaches a module WITHOUT an explicit value for this
// parameter, the commissioning layer (Partner Layer, Phase B) proposes a bounded
// set of values with a written rationale; if the commissioner abstains or fails,
// the runtime falls back to THIS deterministic band. The engine runs one governed
// scenario per value — the band never changes engine math, only which parameters
// are explored, and every explored value is labeled an assumed parameter.
export type ScenarioDefaultBand = {
  requirementId: string;
  values: number[];
  unit?: string;
  label?: string;
  // The recommended point inside the band (e.g. the settle target). Absent ⇒ the
  // runtime uses the median value. Must be one of `values` when present.
  recommendedValue?: number;
  /**
   * #5140 Q6 P0 — the governed binding that PUBLISHES this requirement's value.
   *
   * A scenario case states which parameter it explored. Before this ref, the
   * only way to say so was to restate the raw number on the payload beside the
   * engine-bound bindings — a second numeric authority. With it, the case cites
   * a placeholder and the reader renders the governed binding, so the parameter
   * is fenced exactly like every other figure on the card.
   *
   * DECLARED here, as data, because the link between a requirement id and the
   * placeholder its value is emitted under is a capability fact, not runtime
   * knowledge. Absent ⇒ the producer publishes no input reference at all; it
   * never falls back to a literal number.
   */
  bindingPlaceholderRef?: string;
};

export type ScenarioSpec = {
  scenarioKind: string;
  /**
   * The capability intent ids this scenario spec governs, for modules whose
   * `scenarioKind` is a DOMAIN name rather than an intent id (workforce's
   * `workforce_reduction` vs its `workforce_reduction_scenario` intent, and the
   * same split in capital-allocation, esg-emissions and org-analysis).
   *
   * Consumers that need to find "the scenario spec for this intent" match on
   * `scenarioKind === intentId` OR membership here. Declaring the binding as
   * registry DATA keeps it reviewable next to the parameters it governs, and
   * reusable by every module with the same shape — the alternative is a
   * per-module special case in the runtime, which is the thing this contract
   * exists to prevent. Absent ⇒ only the `scenarioKind === intentId` match
   * applies, exactly as before.
   *
   * Every entry must name an intent DECLARED BY THE SAME CAPABILITY, be
   * non-empty, and appear once — enforced by `validateGovernedCapabilityManifest`
   * so a typo fails the manifest loudly instead of creating a dead binding.
   */
  intentIds?: string[];
  requiredInputs: CapabilityRequirement[];
  optInRequired: boolean;
  // Optional. Present only on scenario kinds that support commissioning: the
  // bounded default parameter bands the commissioning layer explores when the
  // asker gives no explicit parameter. Absent ⇒ the scenario is not commissionable
  // and behaves exactly as before (explicit parameters required).
  defaultBands?: ScenarioDefaultBand[];
};

/**
 * THE scenario-spec-to-intent matching rule. Single-sourced on purpose (#4736).
 *
 * Two consumers must never disagree about which spec governs an intent: the one
 * that BINDS the spec's declared parameters into a call, and the one that decides
 * whether that call is safe to execute with no arguments at all. When they
 * diverge, a spec can be invisible to the safety check while still supplying
 * arguments — so a `required: true` input added later would be bound by one side
 * and declared absent by the other. Both sides call this function.
 *
 * A spec governs an intent when its kind IS the intent id, or when the manifest
 * declares the binding on the spec itself. Defensive about shape because the
 * runtime planner catalog is a projection, not the manifest.
 */
export function scenarioSpecGovernsIntent(
  scenario: Pick<ScenarioSpec, 'scenarioKind' | 'intentIds'> | null | undefined,
  intentId: string
): boolean {
  const targetIntentId = typeof intentId === 'string' ? intentId.trim() : '';
  if (!scenario || targetIntentId.length === 0) return false;
  const scenarioKind =
    typeof scenario.scenarioKind === 'string' ? scenario.scenarioKind.trim() : '';
  if (scenarioKind === targetIntentId) return true;
  const declared = Array.isArray(scenario.intentIds) ? scenario.intentIds : [];
  return declared.some(
    (declaredIntentId) =>
      typeof declaredIntentId === 'string' && declaredIntentId.trim() === targetIntentId
  );
}
